/**
 * Модель зоны: статические методы для работы с иерархией и путём зоны.
 * Данные остаются plain object (удобно для Pinia). Использование: ShopZone.getPath(hierarchy, getTitle).
 */

export class ShopZone {
  private static readonly PATH_SEPARATOR = " ・ ";

  /**
   * Возвращает массив заголовков зон по иерархии (от корня к текущей).
   *
   * @param hierarchy - строка вида "id1.id2.id3", идентификаторы через точку
   * @param getTitle - функция получения заголовка зоны по zone_id
   * @returns массив заголовков зон в порядке от корня к текущей (только существующие title)
   */
  static getTitlesByHierarchy(hierarchy: string, getTitle: (zone_id: string) => string | undefined): string[] {
    if (!hierarchy) return [];

    return hierarchy
      .split(".")
      .map((id) => getTitle(id))
      .filter((t): t is string => Boolean(t));
  }

  /**
   * Формирует полный путь зоны: заголовки всех уровней от корня до текущей, через разделитель " ・ ".
   *
   * @param hierarchy - строка вида "id1.id2.id3", идентификаторы через точку
   * @param getTitle - функция получения заголовка зоны по zone_id
   * @returns строка пути, например "Отдел ・ Секция ・ Стеллаж"
   */
  static getPath(hierarchy: string, getTitle: (zone_id: string) => string | undefined): string {
    return ShopZone.getTitlesByHierarchy(hierarchy, getTitle).join(ShopZone.PATH_SEPARATOR);
  }

  /**
   * Формирует подзаголовок зоны: только заголовки родителей (без текущей зоны), через разделитель " ・ ".
   *
   * @param hierarchy - строка вида "id1.id2.id3", идентификаторы через точку
   * @param getTitle - функция получения заголовка зоны по zone_id
   * @returns строка подзаголовка (родители), например "Отдел ・ Секция" для зоны "Стеллаж"; пустая строка, если родителя нет
   */
  static getSubTitle(hierarchy: string, getTitle: (zone_id: string) => string | undefined): string {
    const titles = ShopZone.getTitlesByHierarchy(hierarchy, getTitle);

    if (titles.length <= 1) return "";

    return titles.slice(0, -1).join(ShopZone.PATH_SEPARATOR);
  }
}
